/**
 * Hero slider (crossfade) — data-hero-slider.
 */
export default function initHeroSlider() {
  const heroRoot = document.querySelector('[data-hero-slider]');
  if (!heroRoot) {
    return;
  }

  const contentSlides = heroRoot.querySelectorAll('[data-hero-content-slide]');
  const figureSlides = heroRoot.querySelectorAll('[data-hero-figure-slide]');
  const dots = heroRoot.querySelectorAll('[data-hero-dot]');
  const prevBtns = document.querySelectorAll('[data-hero-prev]');
  const nextBtns = document.querySelectorAll('[data-hero-next]');
  const total = contentSlides.length;
  if (!total) {
    return;
  }

  let index = 0;
  let autoplayTimer = null;
  let isAnimating = false;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const duration = reducedMotion ? 0 : 480;

  const contentPane = heroRoot.querySelector('[data-hero-content]');
  const figurePane = heroRoot.querySelector('[data-hero-figure]');

  const measurePane = (pane, slides) => {
    if (!pane || !slides.length) {
      return;
    }
    let max = 0;
    slides.forEach((slide) => {
      slide.classList.add('is-measuring');
      max = Math.max(max, slide.offsetHeight);
      slide.classList.remove('is-measuring');
    });
    pane.style.setProperty('--hero-pane-min-h', `${max}px`);
  };

  const syncHeroHeights = () => {
    measurePane(contentPane, contentSlides);
    measurePane(figurePane, figureSlides);
  };

  let resizeTimer;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(syncHeroHeights, 150);
  };

  const updateDots = (i) => {
    dots.forEach((dot, n) => {
      dot.classList.toggle('is-active', n === i);
      dot.setAttribute('aria-selected', n === i ? 'true' : 'false');
    });
  };

  const updateFigureAria = (i) => {
    figureSlides.forEach((slide, n) => {
      slide.setAttribute('aria-hidden', n === i ? 'false' : 'true');
    });
  };

  const crossfade = (nextIndex) => {
    if (nextIndex === index || isAnimating) {
      return;
    }

    if (reducedMotion || duration === 0) {
      index = nextIndex;
      contentSlides.forEach((slide, n) => slide.classList.toggle('is-active', n === index));
      figureSlides.forEach((slide, n) => slide.classList.toggle('is-active', n === index));
      updateDots(index);
      updateFigureAria(index);
      return;
    }

    isAnimating = true;
    const prevIndex = index;
    index = nextIndex;

    contentSlides[prevIndex].classList.add('is-exiting');
    figureSlides[prevIndex]?.classList.add('is-exiting');
    contentSlides[index].classList.add('is-active');
    figureSlides[index]?.classList.add('is-active');

    updateDots(index);
    updateFigureAria(index);

    window.setTimeout(() => {
      contentSlides[prevIndex].classList.remove('is-active', 'is-exiting');
      figureSlides[prevIndex]?.classList.remove('is-active', 'is-exiting');
      isAnimating = false;
    }, duration);
  };

  const goTo = (i) => crossfade((i + total) % total);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  const stopAutoplay = () => {
    if (autoplayTimer) {
      clearInterval(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const startAutoplay = () => {
    if (reducedMotion) {
      return;
    }
    stopAutoplay();
    autoplayTimer = window.setInterval(next, 7000);
  };

  prevBtns.forEach((btn) => btn.addEventListener('click', () => { prev(); startAutoplay(); }));
  nextBtns.forEach((btn) => btn.addEventListener('click', () => { next(); startAutoplay(); }));

  dots.forEach((dot) => {
    dot.addEventListener('click', () => {
      goTo(Number(dot.dataset.heroDot));
      startAutoplay();
    });
  });

  heroRoot.addEventListener('mouseenter', stopAutoplay);
  heroRoot.addEventListener('mouseleave', startAutoplay);

  document.addEventListener('keydown', (e) => {
    if (!heroRoot.matches(':hover') && document.activeElement?.closest('[data-hero-slider]') !== heroRoot) {
      return;
    }
    if (e.key === 'ArrowRight') { next(); startAutoplay(); }
    if (e.key === 'ArrowLeft') { prev(); startAutoplay(); }
  });

  updateDots(0);
  updateFigureAria(0);
  syncHeroHeights();
  window.addEventListener('resize', onResize);
  if (document.fonts?.ready) {
    document.fonts.ready.then(syncHeroHeights);
  }
  startAutoplay();
}
